import {Component, HostListener, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {ComponentCanDeactivate} from 'src/app/common/can-deactivate/component-can-deactivate';
import {AlarmDefinition} from 'src/app/model/alarm-definition';
import {AlarmPersistRequest} from 'src/app/model/requests/alarm-persist-request';
import {AlarmsService} from 'src/app/services/alarms.service';
import {CommonMessagesService} from 'src/app/services/common-messages.service';
import {HierarchyService} from 'src/app/services/hierarchy.service';
import {Operation} from 'src/app/model/operation'
import {MachinesService} from 'src/app/services/machines.service';
import {Subscription} from 'rxjs';
import {noSpecialCharacters, noWhitespaces} from "../../../../shared/custom-validators";
import {AppConfig} from "../../../../shared/global-variables";
import {isPositiveInteger} from "../../../../common/zmx-validators";
import {AlarmType} from "../../../../model/alarm-type";
import {CommonTranslationsService} from "../../../../services/common-translations.service";

@Component({
  selector: 'app-alarm-definition-form',
  templateUrl: './alarm-definition-form.component.html',
  styleUrls: ['./alarm-definition-form.component.scss']
})
export class AlarmDefinitionFormComponent implements OnInit, ComponentCanDeactivate {

  public mode: 'create' | 'edit';
  public alarmForm: FormGroup;

  public infoExpanded: boolean = false;

  public alarmDefinitionUuid: string;
  public alarmDefinition: AlarmDefinition;

  public organizationOwner: any;
  public organizationsAllowed: any[] = [];
  public organizationsHierarchy: any[] = [];
  public organizationsAllowedHierarchy: any[] = [];
  public alarmTypeOptions: { label: string, value: AlarmType }[] = [];
  public operationOptions: { label: string, value: Operation }[] = [];
  public deviceMeasurements: any[] = [];

  public isAlarmLoading: boolean = false;
  public isRoot: boolean;
  public rootLabel: string;
  public persistingChanges: boolean = false;
  public formSuccess: boolean = false;

  public isByteMaskOperation: boolean = false;
  public withTimerSelected: boolean = false;

  private flattenedOrganizationsHierarchy: any[];

  private operationSubscription: Subscription;
  private timerSubscription: Subscription;


  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly i18n: TranslocoService,
    private readonly alarmsService: AlarmsService,
    private readonly commonMessagesService: CommonMessagesService,
    private readonly hierarchyService: HierarchyService,
    private readonly machinesService: MachinesService,
    private readonly commonTranslationsService: CommonTranslationsService
  ) {
  }

  ngOnInit(): void {
    this.initForm();
    this.buildTreeNode();
    this.buildAlarmDeviceMeasurementOptions();
  }

  private initForm(): void {
    this.alarmForm = this.fb.group({
      name: this.fb.control(null, [Validators.maxLength(255), Validators.required,
        noSpecialCharacters, noWhitespaces, Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH)]),
      deviceMeasurement: this.fb.control(null, [Validators.required]),
      operation: this.fb.control(null, [Validators.maxLength(255), Validators.required]),
      value: this.fb.control(null, [Validators.maxLength(255), Validators.required]),
      organizationOwnerUuid: this.fb.control(null, [Validators.required]),
      organizationsAllowedUuids: this.fb.control(null),
      alarmType: this.fb.control(null, [Validators.required]),
      global: this.fb.control(null),
      timer: this.fb.control(null, [Validators.required]),
      timeWindow: this.fb.control(null, [isPositiveInteger()])
    });

    this.operationSubscription = this.alarmForm.get('operation').valueChanges.subscribe((value) => {
      const isByteMaskOperation: boolean = value === 'BYTE_MASK';
      if (this.isByteMaskOperation != isByteMaskOperation) {
        this.alarmForm.get('value').setValue(null);
        this.isByteMaskOperation = isByteMaskOperation;
      }
    });

    this.timerSubscription = this.alarmForm.get('timer').valueChanges.subscribe((value) => {
      const withTimerSelected = value === true;
      if (this.withTimerSelected != withTimerSelected) {
        this.withTimerSelected = withTimerSelected;
        if (withTimerSelected) {
          this.alarmForm.get('operation').setValue('BYTE_MASK');
          this.alarmForm.get('operation').disable();
        } else {
          this.alarmForm.get('operation').enable();
        }
      }
    });
  }

  private loadAlarmDefinition(alarmDefinitionUuid: string): void {
    this.isAlarmLoading = true;
    this.alarmsService.getAlarmDefinitionByUuid(alarmDefinitionUuid).subscribe({
      next: response => {
        this.alarmDefinition = response.alarmDefinition;

        this.alarmForm.patchValue({
          ...response.alarmDefinition,
          timer: response.alarmDefinition.timeWindow != undefined,
          organizationOwnerUuid: response.alarmDefinition.organizationOwner.organizationUuid,
          organizationsAllowedUuids: response.alarmDefinition.organizationsAllowed.map(o => o.organizationUuid)
        })
        this.alarmForm.get('name').disable();
        this.alarmForm.get('deviceMeasurement').disable();
        this.alarmForm.get('timer').disable();

        this.loadDropdowns();
        this.initOrganizationInputs(response.alarmDefinition);

      },
      error: error => {
        if (error.status == 404) {
          this.router.navigate(['/not-found']);
        }
      }
    }).add(() => {
      this.isAlarmLoading = false;
    })
  }

  private loadDropdowns(): void {
    this.buildAlarmTypeOptions();
    this.buildAlarmOperationOptions();
  }

  private buildTreeNode(): void {
    this.hierarchyService.getHierarchyDropdown().subscribe(response => {
      this.flattenedOrganizationsHierarchy = response.hierarchyDto.childs;
      this.isRoot = response.hierarchyDto.organizationType.root;
      this.organizationsHierarchy = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto)[1];
      this.rootLabel = this.organizationsHierarchy.length > 0 ? this.organizationsHierarchy[0].label : '';

      this.activatedRoute.data.subscribe(data => {
        this.mode = data.mode;
        if (this.mode == 'edit') {
          this.infoExpanded = false;
          this.activatedRoute.params.subscribe(params => {
            this.alarmDefinitionUuid = params.alarmDefinitionUuid;
            this.loadAlarmDefinition(params.alarmDefinitionUuid);
          });
        } else {
          this.infoExpanded = true;
          this.alarmForm.get('timer').setValue(false);
          this.alarmForm.get('global').disable();
          this.loadDropdowns();
        }
      });
    });
  }

  private buildAlarmOperationOptions(): void {
    this.commonTranslationsService.getAlarmOperationOptions().subscribe(operationsOptions => {
      this.operationOptions = operationsOptions;
    });
  }

  private buildAlarmTypeOptions(): void {
    this.commonTranslationsService.getAlarmTypeOptions().subscribe(alarmTypeOptions => {
      this.alarmTypeOptions = alarmTypeOptions;
    });
  }

  private buildAlarmDeviceMeasurementOptions(): void {
    this.machinesService.getAlarmDeviceMeasurements().subscribe(response => {
      this.deviceMeasurements = response.deviceMeasurements.map(measurement => ({name: measurement}));
    });
  }

  private initOrganizationInputs(alarmDefinition: AlarmDefinition): void {
    this.organizationOwner = this.getOrganizationOfHierarchy(alarmDefinition.organizationOwner.organizationUuid);
    this.checkGlobal(this.organizationOwner.label);
    this.organizationsAllowedHierarchy = this.organizationOwner.children;
    this.organizationsAllowed = alarmDefinition.organizationsAllowed.map(o => {
      return this.getOrganizationOfHierarchy(o.organizationUuid);
    });
  }

  private checkGlobal(organizationOwnerName: string): void {
    if (organizationOwnerName === this.rootLabel) {
      this.alarmForm.get('global').enable();
    } else {
      this.alarmForm.get('global').disable();
      this.alarmForm.get('global').setValue(false);
    }
  }

  private getOrganizationOfHierarchy(organizationUuid: string): any {
    const path = this.findOrganizationHierarchyPath(organizationUuid);
    let organization = this.organizationsHierarchy[0];
    path.forEach(organizationUuid => {
      organization = organization.children.find(o => o.data === organizationUuid);
    });
    return organization;
  }

  private findOrganizationHierarchyPath(organizationUuid: string): string[] {
    const organization = this.flattenedOrganizationsHierarchy.find(o => o.organizationUuid === organizationUuid);
    const rootReached = organizationUuid != null && organization == null;
    if (!rootReached) {
      const organizationParents: string[] = this.findOrganizationHierarchyPath(organization.parentUuid);
      if (organizationParents != null) {
        return organizationParents.concat([organizationUuid]);
      }
    }
    return [];
  }

  public onOrganizationOwnerChange(event: any): void {
    this.organizationOwner = event;
    this.organizationsAllowed = [];
    this.alarmForm.patchValue({organizationOwnerUuid: event.data});
    this.organizationsAllowedHierarchy = event ? event.children : [];
    this.checkGlobal(event.label);
  }

  public onOrganizationsAllowedChange(event: any): void {
    const organizationsAllowedUuids = event.map(o => o.data);
    this.alarmForm.patchValue({organizationsAllowedUuids})
  }

  public isValid(): boolean {
    return this.alarmForm?.valid;
  }

  saveAlarm(): void {
    if (this.mode === 'create') {
      this.persistAlarmData();
    } else if (this.mode === 'edit') {
      this.commonMessagesService.showRequestConfirmation('info.confirmAlarmEdition', () => this.persistAlarmData());
    }
  }

  private persistAlarmData(): void {
    this.persistingChanges = true;
    const persistData: AlarmPersistRequest = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.alarmsService.createAlarmDefinition(persistData).toPromise();
    } else {
      promise = this.alarmsService.editAlarmDefinition(this.alarmDefinitionUuid, persistData).toPromise();
    }

    promise.then(() => {
      if (this.mode === 'create') {
        this.commonMessagesService.showSuccessfullyCreatedMessage();
      } else {
        this.commonMessagesService.showSuccessfullyUpdatedMessage();
      }
      this.formSuccess = true;
      const extras: NavigationExtras = {state: {showView: 'definition'}};
      this.router.navigate(['/maintenance'], extras);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): AlarmPersistRequest {
    const formValue = this.alarmForm.getRawValue();
    return {
      alarmDefinitionUuid: this.mode === 'edit' ? this.alarmDefinitionUuid : null,
      name: formValue.name?.trim(),
      value: formValue.value,
      operation: formValue.operation,
      deviceMeasurement: formValue.deviceMeasurement,
      organizationOwnerUuid: formValue.organizationOwnerUuid,
      organizationsAllowedUuids: formValue.organizationsAllowedUuids,
      alarmType: formValue.alarmType,
      global: formValue.global,
      timeWindow: formValue.timer === true ? formValue.timeWindow : null
    };
  }

  public cancel(): void {
    const extras: NavigationExtras = {state: {showView: 'definition'}};
    this.router.navigate(['/maintenance'], extras);
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || !this.alarmForm.dirty;
  }

  ngOnDestroy(): void {
    if (this.operationSubscription != null) this.operationSubscription.unsubscribe();
  }

}
